import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Table,
  Upload,
  message,
  Select,
  Divider,
} from "antd";

import { DeleteOutlined, EyeOutlined, InboxOutlined } from "@ant-design/icons";
import {
  IPreAuthQueryReply,
  QueryData,
} from "../../../interfaces/IPreAuthQuery"; // Import the QueryData interface
import { IUploadReferral } from "../../../interfaces/IReferral";
import { UploadDocument } from "../../../models/EnhancementSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import FilePreviewModal from "./FilePreviewModal";
import {
  fetchQueries,
  replyResponseToQuery,
} from "../../../models/preAuthQuerySlice";
import { getDocuments } from "../../../apis/schemeService";
import "./PreAuthQueryCss.css";
import { errorMessage } from "../../../utils/notificationUtils";
import { errorMessageModal } from "../../../utils/modalMessage";

const { Dragger } = Upload;

interface Document {
  id: number;
  type?: string;
  file?: any;
  uploaded?: boolean;
  documentId?: string;
  isInitial?: boolean;
}

interface RequiredDocument {
  id: number;
  name: string;
  expiryDate: string;
}
interface ReplyQueryModalProps {
  visible: boolean;
  onCancel: () => void;
  query: QueryData; // This is the selected query passed from QueryTable
  portalMetaDataId: number;
  setIsModalReplyVisible: any;
  setSelectedQuery: any;
}

const ReplyQueryModal: React.FC<ReplyQueryModalProps> = ({
  visible,
  onCancel,
  query,
  portalMetaDataId,
  setIsModalReplyVisible,
  setSelectedQuery,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { enhancement } = useSelector((state: RootState) => state);
  const [tempFileSelection, setTempFileSelection] = useState<any>({
    file: "",
    index: -1,
    documentType: "",
    documentId: ""
  });
  
  const getInitialDocuments = (requiredDocs: RequiredDocument[]) => {
    return requiredDocs && requiredDocs.length > 0
      ? requiredDocs?.map((doc, index) => ({
          id: index + 1,
          type: doc.name,
          file: undefined,
          documentId: doc.id.toString(),
          uploaded: false,
          isInitial: true,
        }))
      : [{ id: 1, type: undefined, file: undefined }];
  };

  const [documents, setDocuments] = useState<Document[]>(
    getInitialDocuments(query?.queryRequiredDocuments || [])
  );

  const renderDocumentType = (doc: Document, index: number) => {
    // For initial rows, show as a span
    if (doc.isInitial) {
      return (
        // <span
        //   style={{
        //     display: 'block',
        //     width: '100%',
        //     padding: '4px 11px',
        //     border: '1px solid #d9d9d9',
        //     borderRadius: '2px',
        //     backgroundColor: '#f5f5f5',
        //     color: 'rgba(0, 0, 0, 0.88)'
        //   }}
        // >
        //   {doc.type}
        // </span>
        <span
          style={{
            display: "block",
            width: "100%",
            padding: "0px 9px",
            // border: '1px solid #d9d9d9',
            // borderRadius: '2px',
            // backgroundColor: '#f5f5f5',
            color: "rgba(0, 0, 0, 0.88)",
          }}
        >
          {doc.type}
        </span>
      );
    }

    // For additional rows, show dropdown
    return (
      <Select
        className="custom-dropdown"
        style={{ width: "100%" }}
        placeholder="Document Type"
        onChange={(value, option) => {
          const selectedOption = Array.isArray(option) ? option[0] : option;
          handleTypeChange(
            value as string,
            index,
            selectedOption?.key as string
          );
        }}
        value={doc.type}
      >
        {documentsForDrawer?.map((document: any) => (
          <Option
            className="custom-dropdown"
            key={document.id}
            value={document.value}
          >
            {document.value}
          </Option>
        ))}
      </Select>
    );
  };

  const [uploadFileModal, setUploadFileModal] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    index: -1,
    // documentType: "",
  });
  const [selectedFile, setSelectedFile] = useState<any>({
    file: {},
    documentType: "",
    index: "",
  });
  const { Option } = Select;
  const [isLoading, setIsLoading] = useState(false);
  const [responseText, setResponseText] = useState(""); // To store the response input
  const [files, setFiles] = useState<any[]>([]); // To store the files uploaded
  const [documentsForDrawer, setDocumentsForDrawer] = useState<Document[]>([]);
  const { patients, auth, hospital } = useSelector((state: RootState) => state);
  // Handle the response text change
  const handleResponseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setResponseText(e.target.value);
  };

  const schemeMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

  useEffect(() => {
    if (portalMetaDataId && schemeMetaDataId) {
      const fetchDocuments = async () => {
        try {
          const data = await getDocuments(portalMetaDataId, schemeMetaDataId);
          const uniqueDocuments = Object.values(data)
            .flat()
            ?.filter(
              (doc: any, index: number, self: any[]) =>
                index === self?.findIndex((t: any) => t.label === doc.label)
            )
            ?.map((doc: any) => ({ id: doc.value, value: doc.label }));

          setDocumentsForDrawer(uniqueDocuments);
        } catch (err) {
          message.error("Failed to fetch documents.");
        }
      };

      fetchDocuments();
    }
  }, [portalMetaDataId, schemeMetaDataId]);

  const handleUpload = async () => {
    const CollectPostData = new FormData();

    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString()
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id.toString()
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
      );
    }
  
    // Use the temporary file selection
    CollectPostData.append(
      "documentType",
      tempFileSelection.documentId || "OTHERS"
    );
  
    if (tempFileSelection.file) {
      CollectPostData.append("file", tempFileSelection.file);
    }
  
    try {
      const resp = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        })
      ).unwrap();
  
      if (resp?.documentUrl) {
        message.success(`${resp?.documentLabelType} uploaded successfully`);
        
        // Now update the documents array with the file after successful upload
        setDocuments((prev) => {
          const updated = [...prev];
          updated[tempFileSelection.index] = {
            ...updated[tempFileSelection.index],
            file: tempFileSelection.file,
            uploaded: true
          };
          return updated;
        });
        
        setDocumentUrls((prev) => [
          ...prev,
          {
            documentType: resp.documentType || "Others",
            documentName: resp.documentName || "Others",
            documentUrl: resp.documentUrl,
            id: resp.id,
            referenceType: resp.referenceType || null,
            documentLabelType: resp.documentLabelType || null,
            deficiencyCheckStatus: resp.deficiencyCheckStatus || null,
            remarks: resp.remarks || null,
          },
        ]);
      } else {
        message.error("Please Upload Again");
      }
      setUploadFileModal(false);
      // Reset temporary selection
      setTempFileSelection({
        file: null,
        index: -1,
        documentType: "",
        documentId: ""
      });
    } catch (error) {
      console.error("Failed to upload document:", error);
      message.error("Failed to upload document. Please try again.");
    }
  };
  
  const [documentUrls, setDocumentUrls] = useState<
    {
      documentType: string;
      documentName: string;
      documentUrl: string;
      id: number;
      referenceType: string | null;
      documentLabelType: string | null;
      deficiencyCheckStatus: string | null;
      remarks: string | null;
    }[]
  >([]);

  const handleAddRow = (): void => {
    setDocuments((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        type: undefined,
        file: undefined,
        uploaded: false,
        isInitial: false,
      },
    ]);
  };

  const handleFileChange = (file: File, index: number): void => {
    // Store the selected file in temporary state rather than immediately updating documents
    setTempFileSelection({
      file: file,
      index: index,
      documentType: documents[index]?.type || "OTHERS",
      documentId: documents[index]?.documentId
    });
  
    setPreviewFile({
      url: URL.createObjectURL(file),
      name: file.name,
      showUpload: true,
      index: index,
    });
  
    setSelectedFile({
      file: file,
      documentType: documents[index]?.type || "OTHERS",
      index: index,
    });
  
    setUploadFileModal(true);
  };
  

  const handleTypeChange = (
    value: string,
    index: number,
    documentId?: any
  ): void => {
    const updatedDocs = [...documents];
    updatedDocs[index] = {
      ...updatedDocs[index],
      type: value,
      documentId: documentId,
      uploaded: false, // Store the document ID
    };
    setDocuments(updatedDocs);
  };

  // Columns for the document table
  const columns = [
    {
      title: "Document Type",
      dataIndex: "documentType",
      key: "documentType",
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
    },
    {
      title: "Action",
      key: "action",
      render: (text: any, record: any) => (
        <Button
          type="link"
          onClick={() => {
            // Logic to preview the file, if necessary
            message.info(`Previewing ${record.fileName}`);
          }}
        >
          Preview
        </Button>
      ),
    },
  ];

  // const handleDeleteRow = (index: number): void => {
  //   const docId = documentUrls[index]?.id;

  //   setDocuments((prev) => prev.filter((_, idx) => idx !== index));
  //   setDocumentUrls((prev) => prev.filter((_, idx) => idx !== index));

  // };

  const handleDeleteRow = (index: number): void => {
    const docId = documentUrls[index]?.id;

    // Remove from documentUrls
    const updatedDocumentUrls = documentUrls?.filter((_, idx) => idx !== index);
    setDocumentUrls(updatedDocumentUrls);

    // Reset the specific document's file and uploaded status
    const updatedDocuments = documents?.map((doc, idx) =>
      idx === index ? { ...doc, file: undefined, uploaded: false } : doc
    );
    setDocuments(updatedDocuments);

    // Optionally, make an API call to delete the document on the server
    // dispatch(deleteDocument(docId)); // Implement this action if required
  };

  const handleReplyQuery = () => {
    setIsLoading(true);
    const allDocumentsUploaded = documents?.every(
      (doc) => doc.type !== undefined && doc.uploaded === true
    );

    if (!allDocumentsUploaded) {
      errorMessageModal("Please upload all required documents before submitting.");
      setIsLoading(false);
      return;
    }

    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const casePreAuthQueryId = query?.id;
    if (caseId && visitId && casePreAuthQueryId) {
      const replyData: IPreAuthQueryReply = {
        returnedResponse: responseText,
        returnedDocumentUrl: documentUrls,
      };
      dispatch(
        replyResponseToQuery({
          caseId,
          visitId,
          portalMetaDataId,
          replyData,
          casePreAuthQueryId,
        })
      )
        .unwrap()
        .then(() => {
          setResponseText("");
          setDocumentUrls([]);
          setSelectedQuery(null);
          setIsLoading(false);
          // dispatch(fetchQueries({ caseId, visitId, portalMetaDataId }));
          message.success("Query is successfully Responded");
          setIsModalReplyVisible(false);
        })
        .catch((error) => {
          message.error("Failed to Submit Query");
          setIsLoading(false);
          console.error(error);
        });
    }
  };

  const handleBrowseClick = (index: number) => {
    const doc = documents[index];

    // Check if document type is selected
    if (!doc.type) {
      errorMessageModal("Please select document type first");
      return
    }

    // If document type is selected, proceed with file browsing
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = (e: any) => handleFileChange(e.target.files[0], index);
    input.click();
  };

  const closeModal = () => {
    setUploadFileModal(false);
    setTempFileSelection({
      file: null,
      index: -1,
      documentType: "",
      documentId: ""
    });
  };
  return (
    <Modal
      //   title="Reply to Query"
      visible={visible}
      centered
      style={{ boxShadow: "0px 2px 8px 0px #00000040" }}
      onCancel={onCancel}
      footer={[
        <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
          <Button
            key="back"
            onClick={onCancel}
            style={{
              borderColor: "#D2691E",
              color: "#FB923C",
              boxShadow: "0px 2px 8px 0px #00000040",
              width: "25%",
            }}
          >
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            style={{
              backgroundColor: "#D2691E",
              color: "#fff",
              boxShadow: "0px 2px 8px 0px #00000040",
              width: "25%",
            }}
            onClick={handleReplyQuery}
            loading={isLoading}
          >
            Submit Response
          </Button>
        </div>,
      ]}
      width={800}
    >
      <div style={{ marginBottom: "2rem" }}>
        <p
          style={{
            marginBottom: "0.6rem",
            fontSize: "1rem",
            fontWeight: "700",
          }}
        >
          {"Query Response / Reply"}
        </p>
        <Input.TextArea
          value={responseText}
          onChange={handleResponseChange}
          placeholder="Type your response here..."
          rows={5}
        />
      </div>
      <p
        style={{ marginBottom: "0.7rem", fontSize: "1rem", fontWeight: "700" }}
      >
        Required Documents
      </p>
      {/* Documents table */}
      <div
        style={{
          borderRadius: "8px",
          border: "1px solid #D9D9D9",
          padding: "16px",
          marginBottom: "16px",
        }}
      >
        {/* <div style={{ marginBottom: "16px", fontWeight: "600" }}>
              DOCUMENTS :
            </div> */}

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px",
                  borderTop: "1px solid #D9D9D9",
                  borderBottom: "1px solid #D9D9D9",
                  borderLeft: "1px solid #D9D9D9",
                  width: "30%", // Fixed width for the first column
                }}
              >
                Document Type
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px",
                  borderTop: "1px solid #D9D9D9",
                  borderBottom: "1px solid #D9D9D9",
                  width: "50%", // Fixed width for the second column
                }}
              >
                Selected File
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "8px",
                  borderTop: "1px solid #D9D9D9",
                  borderBottom: "1px solid #D9D9D9",
                  borderRight: "1px solid #D9D9D9",
                  width: "20%", // Fixed width for the third column
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {documents?.map((doc, index) => (
              <tr key={doc.id}>
                <td
                  style={{
                    padding: "8px",
                    borderTop: "1px solid #D9D9D9",
                    borderBottom: "1px solid #D9D9D9",
                    borderLeft: "1px solid #D9D9D9",
                    overflow: "hidden", // Ensure no overflow
                    textOverflow: "ellipsis", // Ellipsis for overflow
                    whiteSpace: "nowrap", // Prevent wrapping
                  }}
                >
                  {renderDocumentType(doc, index)}
                </td>
                <td
                  style={{
                    padding: "15px",
                    // borderTop: "1px solid #D9D9D9",
                    borderBottom: "1px solid #D9D9D9",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap", // Prevent text wrapping
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "#1677FF",
                      fontWeight: "600",
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%", // Ensure it respects column width
                    }}
                    title={doc.file ? doc.file.name : "Browse File"}
                    onClick={() => handleBrowseClick(index)}
                  >
                    {doc.file ? doc.file.name : "Browse File"}
                  </span>
                </td>
                <td
                  style={{
                    padding: "8px",
                    borderTop: "1px solid #D9D9D9",
                    borderBottom: "1px solid #D9D9D9",
                    borderRight: "1px solid #D9D9D9",
                    textAlign: "center",
                    overflow: "hidden",
                  }}
                >
                  <Button
                    icon={<EyeOutlined />}
                    style={{
                      border: "none",
                      background: "none",
                      boxShadow: "none",
                    }}
                    disabled={!doc.file}
                    onClick={() => window.open(URL.createObjectURL(doc.file))}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    style={{
                      border: "none",
                      background: "none",
                      boxShadow: "none",
                    }}
                    danger
                    disabled={!doc.file}
                    // style={{ marginLeft: "8px" }}
                    onClick={() => handleDeleteRow(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add Row Button */}
        <div
          style={{
            display: "flex",
            // justifyContent: "center",
            marginTop: "16px",
          }}
        >
          <Button
            type="primary"
            style={{
              backgroundColor: "#008B8B",
              color: "#fff",
              boxShadow: "0px 2px 8px 0px #00000040",
            }}
            onClick={handleAddRow}
          >
            ADD MORE DOCUMENT
          </Button>
        </div>
      </div>
      <FilePreviewModal
        isVisible={uploadFileModal}
        onClose={closeModal}
        fileUrl={previewFile.url}
        fileName={previewFile.name}
        // documentType={previewFile.documentType}
        handleUpload={handleUpload}
        showUploadButton={previewFile.showUpload}
        key={selectedFile?.index || "no-file"}
        isLoading={enhancement?.isLoading}
      />
    </Modal>
  );
};

export default ReplyQueryModal;
